import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Sparkles, ArrowRight, Play, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AuroraBackground from "@/components/AuroraBackground";

export default function Login() {
  const navigate = useNavigate();
  const { login, verify2faLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [step, setStep] = useState("credentials");
  const [pending2fa, setPending2fa] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) {
        setPending2fa(res.pending_2fa_token);
        setStep("totp");
        toast.info("Code 2FA requis");
        return;
      }
      sessionStorage.setItem("prosperr_show_welcome", "1");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2faLogin(pending2fa, code);
      sessionStorage.setItem("prosperr_show_welcome", "1");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const demoLogin = async () => {
    setDemoLoading(true);
    try {
      await login("demo@prosperr.fr", "Demo2026!");
      sessionStorage.setItem("prosperr_show_welcome", "1");
      navigate("/dashboard");
    } catch {
      toast.error("Compte démo indisponible. Réessayez dans un instant.");
    } finally {
      setDemoLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputClass = "h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-base bg-transparent text-[var(--text)]";
  const labelClass = "block hp-label mb-2";

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] antialiased grid lg:grid-cols-2">
      {/* Form side */}
      <div className="flex flex-col min-h-screen">
        <div className="px-6 py-4 border-b border-[var(--border)]">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="brand-link">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-display font-semibold text-xl tracking-tight">Prosperr</span>
          </Link>
        </div>

        <div className="flex-1 grid place-items-center px-6 py-12 relative">
          <div className="brand-glow absolute inset-0 pointer-events-none opacity-50" />
          <div className="w-full max-w-md relative">
            {step === "credentials" ? (
              <>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
                  Connexion à votre espace Prosperr.
                </h1>
                <p className="mt-4 text-base text-zinc-400">
                  Retrouvez vos leads, votre pipeline et vos rendez-vous commerciaux en quelques secondes.
                </p>

                <form onSubmit={submit} className="mt-10 space-y-5">
                  <div>
                    <label htmlFor="email" className={labelClass}>Email</label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="password" className={labelClass}>Mot de passe</label>
                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className={inputClass} />
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => toast.info("La récupération par email arrive très prochainement. Pour réinitialiser votre mot de passe, contactez-nous à support@prosperr.fr.")}
                        data-testid="forgot-password-link"
                        className="text-[12px] font-medium text-zinc-400 hover:text-emerald-300 transition-colors underline underline-offset-4 decoration-zinc-700 hover:decoration-emerald-500"
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} data-testid="login-submit" className="hp-btn hp-btn-primary w-full h-14 text-base mt-6">
                    {loading ? "Connexion…" : "Se connecter"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[11px] text-zinc-500 leading-relaxed text-center mt-4" data-testid="login-rgpd-notice">
                    En vous connectant, vous acceptez nos{" "}
                    <Link to="/privacy" className="text-zinc-300 underline underline-offset-2 hover:text-white">conditions d'utilisation</Link>
                    {" "}et notre{" "}
                    <Link to="/privacy" className="text-zinc-300 underline underline-offset-2 hover:text-white">politique de confidentialité</Link>.
                    Hébergement UE · Aucun tracker publicitaire.
                  </p>
                </form>

                <div className="my-8 flex items-center gap-4 hp-label">
                  <div className="h-px bg-[var(--border)] flex-1" />ou<div className="h-px bg-[var(--border)] flex-1" />
                </div>

                <button onClick={googleLogin} data-testid="google-login" className="hp-btn hp-btn-secondary w-full h-14 text-base">
                  Continuer avec Google
                </button>

                <button type="button" onClick={demoLogin} disabled={demoLoading} data-testid="demo-login"
                  className="mt-3 hp-btn hp-btn-secondary w-full h-14 text-base disabled:opacity-60">
                  <Play className="w-4 h-4 fill-current" />
                  {demoLoading ? "Chargement de la démo…" : "Tester avec un compte démo"}
                </button>
                <p className="text-xs text-zinc-500 text-center mt-3">
                  Aucune inscription · profil BTP préfilé · 6 leads d'exemple
                </p>

                <p className="text-sm text-zinc-400 text-center mt-10">
                  Pas encore de compte ?{" "}
                  <Link to="/register" className="text-emerald-400 font-semibold underline underline-offset-4 hover:text-emerald-300" data-testid="register-link">
                    Créer un compte
                  </Link>
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-emerald-500 grid place-items-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="hp-label" style={{ color: "#86efac" }}>Vérification 2FA</div>
                    <div className="font-display font-semibold text-lg leading-tight">Code à 6 chiffres</div>
                  </div>
                </div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-[1.05]">
                  Entrez le code de votre application.
                </h1>
                <p className="mt-3 text-sm text-zinc-400">
                  Ouvrez Google Authenticator, Authy ou 1Password et tapez le code affiché pour <strong className="text-zinc-300">{email}</strong>.
                </p>
                <form onSubmit={submit2fa} className="mt-8 space-y-5">
                  <div>
                    <label htmlFor="code" className={labelClass}>Code 2FA</label>
                    <Input id="code" autoFocus inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))} placeholder="123 456" data-testid="login-2fa-code" maxLength={8}
                      className="h-14 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-2xl tracking-[0.35em] font-mono bg-transparent text-[var(--text)]"
                    />
                    <p className="text-[11px] text-zinc-500 mt-2">Vous pouvez aussi utiliser un code de secours (8 caractères).</p>
                  </div>
                  <button type="submit" disabled={loading || code.length < 6} data-testid="login-2fa-submit" className="hp-btn hp-btn-primary w-full h-14 text-base mt-4">
                    {loading ? "Vérification…" : "Vérifier et continuer"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
                <button type="button" onClick={() => { setStep("credentials"); setCode(""); setPending2fa(""); }}
                  className="mt-6 text-sm text-zinc-400 hover:text-white underline underline-offset-4" data-testid="login-2fa-back">
                  ← Retour à la connexion
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Visual side */}
      <div className="hidden lg:block relative bg-black overflow-hidden border-l border-[var(--border)]">
        <AuroraBackground intensity={1} />
        <div className="absolute bottom-10 left-10 right-10 text-white z-10">
          <div className="hp-label mb-3" style={{ color: "#86efac" }}>Le copilote commercial</div>
          <div className="font-display text-2xl lg:text-3xl font-semibold leading-tight max-w-md">
            Chaque commercial mérite son copilote dans la poche.
          </div>
        </div>
      </div>
    </div>
  );
}
